import { Suspense, useState } from 'react'
import { useReactiveVar } from '@apollo/client'
import { Box, Grid, Stack } from '@mui/material'
import { Loader, SidebarNavigation, UserSettingsDialog } from 'components'
import { TimeTrackerProvider } from 'components/TimeTracker/TimeTrackerProvider'
import { userSettingsDialogVisibleVar } from 'context'
import { useAuthorizedFeature } from 'hooks'
import { useInvalidateQueriesOnUserChange } from 'hooks/useInvalidateQueriesOnUserChange'
import ReactCursorPosition from 'react-cursor-position'
import { Outlet } from 'react-router'
import { DEFAULT_SPACING } from 'styles'
import { SESSION_INACTIVE_RESCUE_TIME } from 'utility/defaults'
import { lazyWithReload } from 'utility/lazyWithReload'
import { FeatureGrant } from 'types'

// The assistant drags in the stream-chat stack, so it is only loaded once the
// user opens it for the first time.
const ChatbotAssistant = lazyWithReload(() =>
  import('screens/Chatbot/Assistant').then((module) => ({ default: module.ChatbotAssistant }))
)

export const PrivatePageLayout = () => {
  useInvalidateQueriesOnUserChange()

  const userSettingsOpen = useReactiveVar(userSettingsDialogVisibleVar)
  const [isAssistantOpen, setIsAssistantOpen] = useState(false)
  const [assistantMounted, setAssistantMounted] = useState(false)
  const timeTrackerAuthorized = useAuthorizedFeature(FeatureGrant.HomeTimeTracker)

  const pageContent = (
    <>
      <Grid container sx={{ flexWrap: 'nowrap' }}>
        <Grid item>
          <SidebarNavigation
            isAssistantOpen={isAssistantOpen}
            onToggleAssistant={() => {
              setAssistantMounted(true)
              setIsAssistantOpen((open) => !open)
            }}
          />
        </Grid>
        <Grid flex={1} item>
          <Stack paddingRight={DEFAULT_SPACING} paddingLeft={DEFAULT_SPACING} height="100vh">
            <Suspense fallback={<Loader />}>
              <Outlet />
            </Suspense>
          </Stack>
        </Grid>
      </Grid>
      <UserSettingsDialog
        open={userSettingsOpen}
        onClose={() => userSettingsDialogVisibleVar(false)}
      />
      {assistantMounted && (
        <Suspense fallback={null}>
          <ChatbotAssistant open={isAssistantOpen} onClose={() => setIsAssistantOpen(false)} />
        </Suspense>
      )}
    </>
  )

  return timeTrackerAuthorized ? (
    // <TimeTrackerProvider> needs to be first and direct child of <ReactCursorPosition> in order to pass down `isActive` prop
    <ReactCursorPosition hoverOffDelayInMs={SESSION_INACTIVE_RESCUE_TIME}>
      <TimeTrackerProvider>
        <Box sx={{ userSelect: 'text' }}>{pageContent}</Box>
      </TimeTrackerProvider>
    </ReactCursorPosition>
  ) : (
    pageContent
  )
}
